/**
 * Automations domain — cache invalidation registry.
 *
 * Defines which queries every wrapped automations mutation should
 * invalidate on success. Consumed by `hooks.ts` via
 * `useInvalidatingMutation`. The compiler enforces that every entry in
 * `AutomationsMutationId` has a registry entry, so adding a new mutation
 * without declaring its invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import {
  getAutomationQueryKey,
  listAutomationsQueryKey,
} from '../generated/hooks/automationsController'
import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'

// Table-scoped query keys still keyed by this string (DataTable caches in
// AutomationsTable). Substring match covers any sub-keys.
const AUTOMATIONS_TABLE_KEY = 'automations-table'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped automations mutation. Adding a new
 * mutation requires a member here and a corresponding entry in
 * `invalidationRegistry`.
 */
export type AutomationsMutationId =
  | 'createAutomation'
  | 'createAutomationMultipart'
  | 'updateAutomation'
  | 'updateAutomationMultipart'
  | 'partialUpdateAutomation'
  | 'deleteAutomation'
  | 'deleteAutomationAttachment'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys every automation mutation should invalidate.
 *
 * Includes the generated list key plus the legacy `['automations']`
 * string key, which the hand-written `useQuery` in `AutomationsTable`
 * still uses (`['automations', page, params]`) during the migration
 * window. `invalidateQueries` prefix-matches, so the table refetches.
 */
export function commonAutomationInvalidations(): QueryKey[] {
  return [listAutomationsQueryKey(), ['automations']]
}

// ── Registry ────────────────────────────────────────────────────────

export const invalidationRegistry: Record<
  AutomationsMutationId,
  InvalidationRule<any>
> = {
  createAutomation: {
    invalidates: () => commonAutomationInvalidations(),
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  createAutomationMultipart: {
    invalidates: () => commonAutomationInvalidations(),
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  updateAutomation: {
    invalidates: (variables: { id: number }) => [
      ...commonAutomationInvalidations(),
      getAutomationQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  updateAutomationMultipart: {
    invalidates: (variables: { id: number }) => [
      ...commonAutomationInvalidations(),
      getAutomationQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  partialUpdateAutomation: {
    invalidates: (variables: { id: number }) => [
      ...commonAutomationInvalidations(),
      getAutomationQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  deleteAutomation: {
    invalidates: () => commonAutomationInvalidations(),
    removes: () => [containsKeyFilter(AUTOMATIONS_TABLE_KEY)],
  },
  deleteAutomationAttachment: {
    // Removing an attachment changes the parent automation's detail
    // (its `attachments` array), so refresh that detail query.
    invalidates: (variables: { automation_pk: number }) => [
      getAutomationQueryKey({ id: variables.automation_pk }),
    ],
  },
}
